import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Sparkles, ArrowRight, MapPin, Utensils, Car, UserPlus, Wand2, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import { OrganizationSchema } from '../components/SchemaMarkup';
import { FEATURE_FLAGS } from '../config';
import WaitlistModal from '../components/WaitlistModal';
import CrossBrandPromo from '../components/CrossBrandPromo';

// ── Brand palette (locked spec — see /preview/welcome-final) ──────
const NAVY    = '#1E3F70';   // Cobalt — brand wordmark, AI overlay
const NAVY_LT = '#3158A3';
const PINE    = '#1F5C42';   // Deep Pine — logo, Find-a-Stay overlay
const PINE_LT = '#327A5A';
const PINK    = '#FF5C8A';   // Fruity Pink — Experience accent
const PINK_DK = '#E83E70';
const OFFWHITE = '#F8F5EE';
const GOLD_BG = '#FFC93C';
const GOLD_TX = '#3A2A00';

const ICON_STROKE = 1.5;

// User-supplied photography. Each card gets one image overlaid with a
// brand-coloured gradient at 55%/65% (Version B — photo-forward).
const IMG_STAY = 'https://customer-assets.emergentagent.com/job_07d157c1-ed6d-486a-862d-2e630b888519/artifacts/qhxzzv6h_%20-3.jpeg';
const IMG_AI   = 'https://customer-assets.emergentagent.com/job_07d157c1-ed6d-486a-862d-2e630b888519/artifacts/ofbi9b21_Travel%20aesthetic%20-%20From%20Her%20Travels.jpeg';
const IMG_EXP  = 'https://customer-assets.emergentagent.com/job_07d157c1-ed6d-486a-862d-2e630b888519/artifacts/ub3dkq9x_%20-4.jpeg';

const rgba = (h, a) => {
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
const photoOverlay = (img, c1, c2, a1 = 0.55, a2 = 0.65) =>
  `linear-gradient(135deg, ${rgba(c1, a1)}, ${rgba(c2, a2)}), url("${img}")`;


const WelcomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  // Show the "create an account" reminder only to anonymous visitors.
  // Signed-in users (any role) skip it entirely. We re-check on `storage`
  // + `focus` so multi-tab logins flip the banner away in real time.
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    Boolean(
      typeof window !== 'undefined' &&
        (localStorage.getItem('user_token') || localStorage.getItem('token')),
    ),
  );
  useEffect(() => {
    const sync = () =>
      setIsLoggedIn(
        Boolean(
          localStorage.getItem('user_token') || localStorage.getItem('token'),
        ),
      );
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const handleExperienceMontrealClick = () => {
    if (FEATURE_FLAGS.experienceMontreal) {
      navigate('/experience-montreal');
    } else {
      setWaitlistOpen(true);
    }
  };

  return (
    <div className="min-h-screen p-3 md:p-4" style={{ backgroundColor: OFFWHITE }}>
      <SEO 
        title="Homesbyli - Your Gateway to Unforgettable Montreal Experiences"
        description="Discover quality short-term rentals and curated Montreal experiences. Find your perfect stay or explore the best of Montreal with Homesbyli."
        keywords="Montreal rentals, Montreal experiences, short-term rentals Montreal, Montreal vacation, Montreal accommodation, Experience Montreal"
        url="https://homesbyli.com"
        type="website"
      />
      <OrganizationSchema />

      {/* Anonymous-visitor reminder: every booking guest needs an
          ID-verified account, so we surface the signup CTA up front.
          Hidden the moment any auth token is present (customer/host/admin). */}
      {!isLoggedIn && (
        <div
          className="w-full max-w-7xl mx-auto mt-2 mb-6 rounded-2xl bg-white/80 backdrop-blur border shadow-sm"
          style={{ borderColor: 'rgba(31, 92, 66, 0.18)' }}
          data-testid="signup-reminder-banner"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-3.5">
            <div className="flex items-start sm:items-center gap-3 min-w-0">
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(31, 92, 66, 0.10)' }}
              >
                <UserPlus className="w-4 h-4" style={{ color: PINE }} />
              </span>
              <p className="text-sm text-gray-800 leading-snug">
                <span className="font-semibold text-gray-900">Create an account</span>
                <span className="text-gray-700"> before making your first booking — we verify every guest with government ID for everyone&apos;s safety.</span>
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 self-end sm:self-auto">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm font-medium underline underline-offset-4"
                style={{ color: PINE }}
                data-testid="signup-reminder-login"
              >
                Log in
              </button>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="text-sm font-semibold text-white rounded-full px-4 py-2 transition-colors whitespace-nowrap hover:opacity-90"
                style={{ backgroundColor: NAVY }}
                data-testid="signup-reminder-cta"
              >
                Sign up free
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl w-full mx-auto">
        {/* Header — compact on mobile so the 3 cards fit above the fold */}
        <div className="text-center mb-3 md:mb-12">
          <div className="flex items-center justify-center mb-1 md:mb-4">
            <svg
              className="w-8 h-8 md:w-16 md:h-16"
              viewBox="0 0 32 32"
              fill={PINE}
            >
              <path d="M16 1c2 0 3.46.96 4.75 3.27l.53 1.02a424.58 424.58 0 007.1 13.18 4 4 0 01-6.9 4.01l-.44-.76C19.5 19.5 18 16.84 16 16c-2 .84-3.5 3.5-5.04 5.72l-.44.76a4 4 0 01-6.9-4.01 424.58 424.58 0 007.1-13.18l.53-1.02C12.54 1.96 14 1 16 1z" />
            </svg>
          </div>
          <h1 className="text-2xl md:text-5xl xl:text-6xl font-bold text-gray-900 mb-1 md:mb-4">
            {t('home.welcomeTo')} <span style={{ color: NAVY }}>Homesbyli</span>
          </h1>
          <p className="hidden md:block text-xl text-gray-600 max-w-2xl mx-auto">
            {t('home.gateway')}
          </p>
        </div>

        {/* ═════════════════════ MOBILE LAYOUT (< md) ═════════════════════
            Bigger Option-B list rows + a featured banner highlighting the
            new AI Concierge feature. Desktop intentionally keeps the lush
            3-card layout below (hidden md:grid).  */}
        <div className="md:hidden">
          {/* Featured "NEW" banner — pushes the AI Concierge launch */}
          <button
            type="button"
            onClick={() => navigate('/plan')}
            className="w-full text-left relative overflow-hidden rounded-2xl p-5 mb-5 shadow-xl active:scale-[0.98] transition-transform"
            style={{
              backgroundImage: photoOverlay(IMG_AI, NAVY, NAVY_LT, 0.82, 0.78),
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            data-testid="welcome-ai-new-banner"
          >
            {/* Subtle decorative blob */}
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-30" style={{ backgroundColor: PINK }} />
            <div className="absolute -bottom-12 -left-8 w-32 h-32 rounded-full blur-2xl opacity-25" style={{ backgroundColor: GOLD_BG }} />
            <div className="relative flex items-center gap-4 text-white">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-8 h-8" strokeWidth={ICON_STROKE} />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full mb-1.5 shadow"
                  style={{ backgroundColor: GOLD_BG, color: GOLD_TX }}
                >
                  <Sparkles className="w-3 h-3" strokeWidth={ICON_STROKE} />
                  New · Powered by AI
                </div>
                <h3 className="text-lg font-bold leading-tight">Create your own personalized trip</h3>
                <p className="text-sm text-white/90 mt-0.5">Custom Montreal itinerary in 30 seconds</p>
              </div>
              <ArrowRight className="w-6 h-6 flex-shrink-0" strokeWidth={ICON_STROKE} />
            </div>
          </button>

          {/* Section label */}
          <p className="text-sm text-gray-500 font-medium mb-3 px-1">How can we help today?</p>

          <div className="space-y-3">
            {/* Row: Find a Stay */}
            <button
              type="button"
              onClick={() => navigate('/search')}
              className="w-full bg-white rounded-2xl p-5 flex items-center gap-4 text-left active:scale-[0.98] transition-transform shadow-sm hover:shadow-md"
              style={{ boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px ${rgba(PINE, 0.18)}` }}
              data-testid="welcome-find-stay-mobile"
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 text-white shadow-lg overflow-hidden relative"
                style={{
                  backgroundImage: photoOverlay(IMG_STAY, PINE, PINE_LT),
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <Home className="w-8 h-8 relative z-10 drop-shadow" strokeWidth={ICON_STROKE} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 text-lg leading-tight">Find a Stay</div>
                <div className="text-sm text-gray-600 mt-0.5">Premium apartments · student & medical deals</div>
                <div className="mt-1.5 inline-flex items-center gap-1">
                  <span
                    className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: GOLD_BG, color: GOLD_TX }}
                  >
                    🅿️ Free parking
                  </span>
                  <span
                    className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: GOLD_BG, color: GOLD_TX }}
                  >
                    🥐 Free breakfast
                  </span>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-400 flex-shrink-0" strokeWidth={ICON_STROKE} />
            </button>

            {/* Row: AI Concierge */}
            <button
              type="button"
              onClick={() => navigate('/plan')}
              className="w-full bg-white rounded-2xl p-5 flex items-center gap-4 text-left active:scale-[0.98] transition-transform shadow-sm hover:shadow-md"
              style={{ boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px ${rgba(NAVY, 0.18)}` }}
              data-testid="welcome-ai-concierge-mobile"
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 text-white shadow-lg overflow-hidden relative"
                style={{
                  backgroundImage: photoOverlay(IMG_AI, NAVY, NAVY_LT),
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <Wand2 className="w-8 h-8 relative z-10 drop-shadow" strokeWidth={ICON_STROKE} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-gray-900 text-lg leading-tight">Create your own personalized trip</div>
                </div>
                <div className="text-sm text-gray-600 mt-0.5">AI plans your full Montreal trip</div>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-400 flex-shrink-0" strokeWidth={ICON_STROKE} />
            </button>

            {/* Row: Experience Montreal */}
            <button
              type="button"
              onClick={handleExperienceMontrealClick}
              className="w-full bg-white rounded-2xl p-5 flex items-center gap-4 text-left active:scale-[0.98] transition-transform shadow-sm hover:shadow-md"
              style={{ boxShadow: `0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px ${rgba(PINK_DK, 0.18)}` }}
              data-testid="welcome-experience-montreal-mobile"
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 text-white shadow-lg overflow-hidden relative"
                style={{
                  backgroundImage: photoOverlay(IMG_EXP, PINK_DK, PINK),
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <MapPin className="w-8 h-8 relative z-10 drop-shadow" strokeWidth={ICON_STROKE} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-gray-900 text-lg leading-tight">Experience Montreal</div>
                  {!FEATURE_FLAGS.experienceMontreal && (
                    <span
                      className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: GOLD_BG, color: GOLD_TX }}
                    >
                      Soon
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 mt-0.5">Restaurants, tours & local discounts</div>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-400 flex-shrink-0" strokeWidth={ICON_STROKE} />
            </button>
          </div>
        </div>

        {/* ═══════════════════ DESKTOP LAYOUT (md+) ═══════════════════ */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 max-w-7xl mx-auto">
          
          {/* Find a Stay Block */}
          <div
            onClick={() => navigate('/search')}
            className="group relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl cursor-pointer transform transition-all duration-500 active:scale-[0.98] md:hover:scale-105 md:hover:shadow-3xl min-h-[150px] md:min-h-[500px]"
            data-testid="welcome-find-stay"
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
              style={{ backgroundImage: `url(${IMG_STAY})` }}
            />

            {/* Brand-tinted overlay */}
            <div
              className="absolute inset-0 transition-all duration-500"
              style={{ background: `linear-gradient(135deg, ${rgba(PINE, 0.55)}, ${rgba(PINE_LT, 0.65)})` }}
            />

            {/* Content */}
            <div className="relative h-full flex flex-col justify-between p-5 md:p-10 text-white">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl mb-2 md:mb-6 group-hover:bg-white/30 transition-colors">
                  <Home className="w-6 h-6 md:w-10 md:h-10" strokeWidth={ICON_STROKE} />
                </div>
                <h2 className="text-xl md:text-4xl xl:text-5xl font-bold mb-1 md:mb-4 leading-tight">
                  {t('home.findAStay')}
                </h2>
                <p className="hidden md:block text-sm md:text-lg text-white/90 mb-4">
                  {t('home.findAStayDesc')}
                </p>

                {/* Exclusive Deal Badge — desktop only to keep mobile compact */}
                <div
                  className="hidden md:block px-4 py-3 rounded-lg mb-4 shadow-lg"
                  style={{ background: `linear-gradient(90deg, ${GOLD_BG}, #F59E0B)`, color: GOLD_TX }}
                >
                  <p className="text-sm font-bold">🎉 {t('home.exclusiveDealsBadge')}</p>
                  <p className="text-xs">{t('home.exclusiveDealsBadgeDesc')}</p>
                </div>

                {/* Target Audience Badge */}
                <div className="hidden md:inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30 mb-6">
                  <span className="text-sm font-medium text-white">{t('home.perfectForAudience')}</span>
                </div>
              </div>

              {/* Features List — desktop only */}
              <div className="hidden md:block space-y-3 mb-8">
                {/* Highlighted Montreal-wide perks — top of list */}
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: GOLD_BG }} />
                  <span className="text-white font-semibold">🅿️ Free parking on every Montreal stay</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: GOLD_BG }} />
                  <span className="text-white font-semibold">🥐 Free breakfast on every Montreal stay</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <span className="text-white/90">{t('home.premiumApts')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <span className="text-white/90">{t('home.flexibleBooking')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <span className="text-white/90">{t('home.primeLocations')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: GOLD_BG }} />
                  <span className="text-white font-semibold">🎓 {t('home.studentLeaseDeal')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: GOLD_BG }} />
                  <span className="text-white font-semibold">🏥 {t('home.medicalDiscounts')}</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                className="w-full bg-white py-2.5 md:py-4 px-4 md:px-6 rounded-lg md:rounded-xl font-bold text-sm md:text-lg flex items-center justify-center gap-2 transition-colors shadow-lg"
                style={{ color: PINE }}
              >
                <span className="hidden md:inline">{t('home.browseProperties')}</span>
                <span className="md:hidden">Browse stays</span>
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-2 transition-transform" strokeWidth={ICON_STROKE} />
              </button>
            </div>
          </div>

          {/* AI Concierge Block — plan a full Montreal trip (stay + food
              + activities) with Claude-powered curation. Visually
              distinct from the other two cards via a cobalt navy
              overlay so it reads as a third, equal-weight option
              rather than a sub-feature of either. */}
          <div
            onClick={() => navigate('/plan')}
            className="group relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl cursor-pointer transform transition-all duration-500 active:scale-[0.98] md:hover:scale-105 md:hover:shadow-3xl min-h-[150px] md:min-h-[500px]"
            data-testid="welcome-ai-concierge"
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
              style={{ backgroundImage: `url(${IMG_AI})` }}
            />

            {/* Brand-tinted overlay */}
            <div
              className="absolute inset-0 transition-all duration-500"
              style={{ background: `linear-gradient(135deg, ${rgba(NAVY, 0.55)}, ${rgba(NAVY_LT, 0.65)})` }}
            />

            {/* Content */}
            <div className="relative h-full flex flex-col justify-between p-5 md:p-10 text-white">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl mb-2 md:mb-6 group-hover:bg-white/30 transition-colors">
                  <Wand2 className="w-6 h-6 md:w-10 md:h-10" strokeWidth={ICON_STROKE} />
                </div>
                <h2 className="text-xl md:text-3xl xl:text-4xl font-bold mb-1 md:mb-4 leading-tight">
                  {t('home.aiConcierge', 'Create your own personalized trip')}
                </h2>
                <p className="hidden md:block text-sm md:text-lg text-white/90 mb-4">
                  {t(
                    'home.aiConciergeDesc',
                    'Tell us your dates and vibe — our AI builds a custom Montreal itinerary in seconds.',
                  )}
                </p>

                {/* "New" Badge — desktop only */}
                <div
                  className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 shadow-lg"
                  style={{ backgroundColor: GOLD_BG, color: GOLD_TX }}
                >
                  <Sparkles className="w-4 h-4" strokeWidth={ICON_STROKE} />
                  <span className="text-xs font-bold uppercase tracking-wide">
                    {t('home.aiConciergeBadge', 'New · Powered by Claude')}
                  </span>
                </div>
              </div>

              {/* Features List — desktop only */}
              <div className="hidden md:block space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <span className="text-white/90">
                    {t('home.aiConciergeFeature1', 'Curated stays matched to your trip')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <span className="text-white/90">
                    {t('home.aiConciergeFeature2', 'Restaurants picked for your taste')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <span className="text-white/90">
                    {t('home.aiConciergeFeature3', 'Activities by neighborhood & weather')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: GOLD_BG }} />
                  <span className="text-white font-semibold">
                    {t('home.aiConciergeFeature4', '⚡ Itinerary built in under 30 seconds')}
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                className="w-full bg-white py-2.5 md:py-4 px-4 md:px-6 rounded-lg md:rounded-xl font-bold text-sm md:text-lg flex items-center justify-center gap-2 transition-colors shadow-lg"
                style={{ color: NAVY }}
                data-testid="welcome-ai-concierge-cta"
              >
                <Sparkles className="w-4 h-4 md:hidden" strokeWidth={ICON_STROKE} />
                <span>{t('home.planWithAi', 'Plan with AI')}</span>
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-2 transition-transform" strokeWidth={ICON_STROKE} />
              </button>
            </div>
          </div>

          {/* Experience Montreal Block — always rendered. While the page is
              still under construction (FEATURE_FLAGS.experienceMontreal=false),
              it shows a "Coming soon" treatment and routes to the placeholder
              page. Flip the flag to ship the full experience. */}
          <div
            onClick={handleExperienceMontrealClick}
            className="group relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl cursor-pointer transform transition-all duration-500 active:scale-[0.98] md:hover:scale-105 md:hover:shadow-3xl min-h-[150px] md:min-h-[500px]"
            data-testid="welcome-experience-montreal"
          >
            {/* Coming Soon ribbon — only while the feature is gated off */}
            {!FEATURE_FLAGS.experienceMontreal && (
              <div
                className="absolute top-3 right-3 md:top-5 md:right-5 z-10 text-[10px] md:text-xs font-bold uppercase tracking-wide px-2 py-1 md:px-3 md:py-1.5 rounded-full shadow-lg"
                style={{ backgroundColor: GOLD_BG, color: GOLD_TX }}
                data-testid="welcome-experience-montreal-coming-soon-badge"
              >
                Coming soon
              </div>
            )}
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
              style={{ backgroundImage: `url(${IMG_EXP})` }}
            />

            {/* Brand-tinted overlay */}
            <div
              className="absolute inset-0 transition-all duration-500"
              style={{ background: `linear-gradient(135deg, ${rgba(PINK_DK, 0.55)}, ${rgba(PINK, 0.65)})` }}
            />

            {/* Content */}
            <div className="relative h-full flex flex-col justify-between p-5 md:p-10 text-white">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl mb-2 md:mb-6 group-hover:bg-white/30 transition-colors">
                  <MapPin className="w-6 h-6 md:w-10 md:h-10" strokeWidth={ICON_STROKE} />
                </div>
                <h2 className="text-xl md:text-4xl xl:text-5xl font-bold mb-1 md:mb-4 leading-tight">
                  {t('home.experienceTitle')}
                </h2>
                <p className="hidden md:block text-sm md:text-lg text-white/90 mb-4 md:mb-6">
                  {t('home.experienceDesc')}
                </p>
              </div>

              {/* Features List — desktop only */}
              <div className="hidden md:block space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <Utensils className="w-5 h-5" strokeWidth={ICON_STROKE} />
                  <span className="text-white/90">{t('home.restaurantPartners')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5" strokeWidth={ICON_STROKE} />
                  <span className="text-white/90">{t('home.guidedTours')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Car className="w-5 h-5" strokeWidth={ICON_STROKE} />
                  <span className="text-white/90">{t('home.transportDiscounts')}</span>
                </div>
              </div>

              {/* Discount Badge — desktop only */}
              {FEATURE_FLAGS.experienceMontreal && (
                <div className="hidden md:block mb-4">
                  <div
                    className="inline-block px-4 py-2 rounded-full font-bold text-sm"
                    style={{ backgroundColor: GOLD_BG, color: GOLD_TX }}
                  >
                    {t('home.saveUpTo')}
                  </div>
                </div>
              )}

              {/* CTA Button */}
              <button
                className="w-full bg-white py-2.5 md:py-4 px-4 md:px-6 rounded-lg md:rounded-xl font-bold text-sm md:text-lg flex items-center justify-center gap-2 transition-colors shadow-lg"
                style={{ color: PINK_DK }}
              >
                <span className="hidden md:inline">{FEATURE_FLAGS.experienceMontreal ? t('home.exploreOffers') : 'Get notified when it launches'}</span>
                <span className="md:hidden">{FEATURE_FLAGS.experienceMontreal ? 'Explore' : 'Notify me'}</span>
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-2 transition-transform" strokeWidth={ICON_STROKE} />
              </button>
            </div>
          </div>

        </div>

        {/* Guest rewards promo banner — RESTORED + REBRANDED 2026-07-03.
            Repositioned from "buy gift cards" marketplace framing to
            "automatic guest rewards" platform framing per Giftronaut
            alignment: we don't sell inventory or expose codes; we
            trigger rewards on qualifying booking events (welcome
            breakfast, service recovery, loyalty) that guests redeem
            through the provider's own flow. CTA now routes to /search
            (book a stay to unlock rewards) instead of /dining. */}
        <div className="mt-8 md:mt-12 max-w-7xl mx-auto" data-testid="welcome-guest-rewards-banner">
          <div
            className="rounded-2xl md:rounded-3xl overflow-hidden shadow-xl"
            style={{ background: 'linear-gradient(135deg, #FFF1CC 0%, #FFDB80 50%, #FFC93C 100%)' }}
          >
            <div className="flex flex-col md:flex-row items-center gap-5 md:gap-8 p-6 sm:p-8 md:p-10">
              <div
                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg text-white"
                style={{ background: PINK_DK }}
              >
                <Sparkles className="w-8 h-8 md:w-10 md:h-10" strokeWidth={ICON_STROKE} />
              </div>
              <div className="flex-1 min-w-0 text-center md:text-left">
                <div
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2"
                  style={{ background: 'rgba(255, 255, 255, 0.7)', color: PINE }}
                >
                  New on Homesbyli
                </div>
                <h2 className="text-2xl md:text-3xl xl:text-4xl font-bold text-gray-900 leading-tight mb-1.5">
                  Guest rewards — on the house
                </h2>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed max-w-2xl">
                  Every Homesbyli stay comes with a little extra — a <strong>welcome breakfast</strong>, a coffee on us, a thank-you at checkout — from local restaurants, cafés, and brands we love.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch gap-2.5 md:gap-3 w-full md:w-auto flex-shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/search')}
                  className="px-6 md:px-7 py-3 md:py-3.5 rounded-full text-sm md:text-base font-bold shadow-md hover:shadow-lg transition text-white whitespace-nowrap"
                  style={{ backgroundColor: PINK_DK }}
                  data-testid="welcome-guest-rewards-book"
                >
                  Book a stay →
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/rewards-preview')}
                  className="px-6 md:px-7 py-3 md:py-3.5 rounded-full text-sm md:text-base font-semibold shadow-sm hover:shadow-md transition whitespace-nowrap"
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: PINE,
                    border: `1.5px solid ${PINE}30`,
                  }}
                  data-testid="welcome-guest-rewards-preview"
                >
                  See rewards →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note — hidden on mobile to keep the welcome above the fold */}
        <div className="hidden md:block text-center mt-12">
          <p className="text-gray-600 text-sm">
            ✨ {t('home.footerNote')}
          </p>
        </div>
      </div>

      {/* Cross-brand bridge — surface UCWonder marketplace (gift cards,
          experiences, AI concierge) for guests planning a Montreal trip
          who might want to bundle add-ons with their stay. */}
      <div className="max-w-7xl mx-auto px-5 pb-16">
        <CrossBrandPromo variant="card" testId="welcome-cross-brand-promo" />
      </div>

      <WaitlistModal
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        feature="experience_montreal"
      />
    </div>
  );
};

export default WelcomePage;
